import styled from '@emotion/styled';
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import styles from './index.module.scss';
import ProofOfOwnerShipImg from '../../../../../assets/images/warranty/proof-doc-icon.png';
import ProofOfOwnerShipSuccess from '../../../../../assets/images/warranty/proof-of-doc-success.png';
import ProofOfRoofImg from '../../../../../assets/images/warranty/proof-of-roof.png';
import TransferCardImg from '../../../../../assets/images/warranty/transfer-card-icon.png';
import Accordion from '../../../ComponentLibrary/oc-accordion';
import ReduxFileWidget from '../../shared/redux-file-widget';
import { BackArrow } from '../../shared/svgIcons.jsx';
import { decrementStep, incrementStep, setAppData, setFormData } from '../redux/actions';

const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 35rem;
  width: 100%;

  hr {
    background-color: #000000;
    border: 0;
    clear: both;
    display: block;
    height: 2.5px;
    margin: 1rem 0;
    width: 100%;
  }
`;

const Header = styled.h3`
  font-family: 'OCRoboto';
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
  padding-top: 1rem;
  text-transform: uppercase;
`;

const SubmitButton = styled.button`
  background-color: #d40f7d;
  border: none;
  color: #ffffff;
  cursor: pointer;
  font-size: 18px;
  margin-top: 1rem;
  padding: 0.5rem;
  width: 100%;

  :disabled {
    background-color: #d8d8d8;
    cursor: default;
  }
`;

const InfoBar = styled.div`
  background-color: #f8f8f8;
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
  padding: 1rem;

  > div {
    > p {
      margin: 0;
    }
  }
`;

const BackWrapper = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 2rem;
  width: 100%;
`;

const BlurredText = styled.p`
  filter: blur(2px);
  margin-top: 0;
  padding-bottom: 1.5rem;
`;

const mapStateToProps = state => ({
  formData: state.formData,
});

const capitalizeFirstLetter = string => string.charAt(0).toUpperCase() + string.slice(1);

const Step4 = ({ decrementStep, incrementStep, setFormData, formData }) => {
  const [validated, setValidated] = useState(true);

  const onChangeRequiredFiles = files => {
    setFormData({ proofOfOwnerShip: files });
  };

  const onChangeRoofPurchaseFiles = files => {
    setFormData({ proofOfRoofPurchase: files });
  };

  const onChangeTransferCardFiles = files => {
    setFormData({ proofOfTransferCard: files });
  };

  function validateInputs() {
    setValidated(formData.proofOfOwnerShip);
  }

  useEffect(() => {
    validateInputs();
  }, [formData]);

  function submitForm() {
    setValidated(false);

    const formBody = {
      warranty_transfers: {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone_number: formData.phoneNumber,
        transfer_date: new Date(formData.closeDateYear, formData.closeDateMonth - 1, formData.closeDateDay),
        role: formData.role,
        did_install_roof: formData.didInstallRoof,
        preferred_contact_methods: formData.preferredContactMethods,
        property_address_attributes: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          street_1: formData.propertyAddress.street,
          street_2: formData.propertyAddress.streetLine2,
          city: formData.propertyAddress.city,
          state_province: formData.propertyAddress.state,
          country_code: formData.country,
          postal_code: formData.propertyAddress.zip,
        },
        mailing_address_attributes: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          street_1: formData.ownerAddress.street,
          street_2: formData.ownerAddress.streetLine2,
          city: formData.ownerAddress.city,
          state_province: formData.ownerAddress.state,
          country_code: formData.country,
          postal_code: formData.ownerAddress.zip,
        },
        homeowner_warranty_transfer_documents: {
          proof_of_ownership: formData.proofOfOwnerShip,
          proof_of_purchase: formData.proofOfRoofPurchase,
          transfer_card: formData.proofOfTransferCard,
        },
      },
    };

    fetch(`${MDMS_URL}/api/v1/homeowner/warranty_transfers`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formBody),
    })
      .catch(err => {
        console.log(err);
      })
      .then(result => result.json())
      .then(json => {
        incrementStep();
      });
  }

  return (
    <StepWrapper>
      <BackWrapper
        data-track="form-back"
        data-track-form-name="Warranty Transfer"
        data-track-form-step="step 4 - documentation"
        onClick={decrementStep}
      >
        <BackArrow width="21" height="12" />
        <p>Back</p>
        <BlurredText>When did you close on this property?</BlurredText>
      </BackWrapper>
      <InfoBar>
        <div>
          <strong>Contact Name</strong>
          <p>
            {formData.firstName} {formData.lastName}
          </p>
        </div>
        <div>
          <strong>Property Address</strong>
          <p>
            {formData.propertyAddress.street}{' '}
            {formData.propertyAddress.streetLine2 && formData.propertyAddress.streetLine2}
          </p>
          <p>
            {formData.propertyAddress.city}, {formData.propertyAddress.state}, {formData.propertyAddress.zip}
          </p>
        </div>
      </InfoBar>
      <hr />
      <InfoBar>
        <div>
          <strong>Preferred Method of Contact</strong>
          <p>{capitalizeFirstLetter(formData.userPreferredContact)}</p>
        </div>
        <div>
          <strong>Property Close Date</strong>
          <p>
            {formData.closeDateMonth}/{formData.closeDateDay}/{formData.closeDateYear}
          </p>
        </div>
      </InfoBar>
      <Header>Upload Documentation</Header>
      <p className={styles.colorRequired}>
        In order to process your request for warranty transfer, the following documentation is needed:
      </p>
      <Accordion
        label={
          <>
            Proof of Ownership (<span className={styles.colorRequired}>Required</span>)
          </>
        }
        labelClassName={styles.labelHeader}
        imageSrc={formData.proofOfOwnerShip ? ProofOfOwnerShipSuccess : ProofOfOwnerShipImg}
      >
        <p>
          Please provide one of the following acceptable documents which includes the
          <b> buyer's name, seller's name, Property address, and the closing date.</b>
        </p>
        <ul>
          <li>Closing Disclosure</li>
          <li>Deed</li>
          <li>Sales record</li>
        </ul>
        <ReduxFileWidget onChange={onChangeRequiredFiles} />
      </Accordion>
      <Accordion label="Proof of Purchase (Optional)" labelClassName={styles.labelHeader} imageSrc={ProofOfRoofImg}>
        <ReduxFileWidget onChange={onChangeRoofPurchaseFiles} />
      </Accordion>
      <Accordion label="Transfer Card (Optional)" labelClassName={styles.labelHeader} imageSrc={TransferCardImg}>
        <ReduxFileWidget onChange={onChangeTransferCardFiles} />
      </Accordion>
      <SubmitButton
        data-track="form-submit"
        data-track-form-name="Warranty Transfer"
        data-track-form-step="step 4 - documentation"
        onClick={() => submitForm()}
        disabled={!validated}
      >
        Submit Transfer
      </SubmitButton>
    </StepWrapper>
  );
};

const mapDispatchToProps = dispatch => ({
  decrementStep: () => dispatch(decrementStep()),
  incrementStep: () => dispatch(incrementStep()),
  setAppData: hash => dispatch(setAppData(hash)),
  setFormData: hash => dispatch(setFormData(hash)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Step4);
